// Property lists are explicit: `transition-all` also animates layout
// properties, which is what makes a hover feel cheap on a low-end phone.
//
// prefers-reduced-motion is deliberately NOT handled here. It is one rule in
// global.css that stops keyframes and animated movement while keeping colour
// and opacity, so no token has to remember it.
// @see comment-sicko skill
const EASE = "ease-out"
const INTERACTIVE_PROPS =
  "transition-[color,background-color,border-color,box-shadow,transform,opacity]"
const FEEDBACK = `transition-colors duration-150 ${EASE}`
const STATE = `${INTERACTIVE_PROPS} duration-200 ${EASE}`
const NOTICE = `${INTERACTIVE_PROPS} duration-300 ${EASE}`
const REVEAL = `transition-transform duration-500 ${EASE}`

const ENTRANCE = "animate-fade-in"
const CELEBRATE = "animate-bubble-bounce"

const IMAGE_COVER_ZOOM = `object-cover ${REVEAL} group-hover:scale-110`
const HEADING_BASE = "font-bold text-neutral-900"
const BODY_BASE = "text-neutral-800"
const BG_GRADIENT = "bg-gradient-to-r from-primary-600 to-primary-800"
const ENTRY_PANE = "px-4 py-8 sm:px-8 md:p-10"
// The sign-in band and the manager header are one surface to the browser,
// so signing in morphs one into the other (see global.css). A view-transition
// name must be unique on the page: only these two elements carry it, and they
// are never on screen together.
const HEADER_HANDOFF = "[view-transition-name:manager-header]"

const GAP = {
  xs: "gap-2",
  sm: "gap-4",
  md: "gap-6",
  lg: "gap-8",
  responsive: "gap-6 md:gap-8",
}
// `content-start`: a stack taller than its content (a grid row shared with
// a taller sibling, a `flex-1` column) keeps its rows at their own height
// instead of stretching them, which pushed a short field's control down out
// of line with its neighbour's.
const STACK = "grid grid-cols-1 content-start"

const FOCUS_RING =
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"

export const tokens = {
  // The manager sign-in: a welcome pane in the primary colour over (phone) or
  // beside (md+) a white form pane. Below md the page is the surface, so the
  // welcome band runs edge to edge; from md up both panes join into one card.
  entry: {
    page: "min-h-dvh bg-white md:flex md:items-center md:justify-center md:bg-neutral-100 md:p-8",
    panel:
      "w-full bg-white md:grid md:max-w-4xl md:grid-cols-2 md:overflow-hidden md:rounded-2xl md:border md:border-neutral-200",
    // `md:rounded-l-2xl` repeats the panel's corner: the morph snapshots the
    // band on its own, outside the panel's clip.
    welcome: `${BG_GRADIENT} text-primary-ramp-foreground md:rounded-l-2xl ${HEADER_HANDOFF} ${ENTRY_PANE}`,
    form: ENTRY_PANE,
    // The key stamp: an outline in the pane's own text colour, so it holds
    // contrast whatever primary the tenant picks.
    mark: "inline-flex h-14 w-14 items-center justify-center rounded-full border border-current",
    storeName: "break-words",
  },
  sections: {
    hero: `rounded-xl md:rounded-2xl ${BG_GRADIENT} text-primary-ramp-foreground text-center px-4 py-6 sm:px-8 sm:py-8 md:py-12`,
    managerHeader: `${BG_GRADIENT} text-primary-ramp-foreground shadow-md ${HEADER_HANDOFF}`,
  },
  text: {
    heading: {
      h1: `text-content-800 font-extrabold text-2xl md:text-3xl leading-[1.3] text-pretty`,
      h1OnPrimary:
        "text-primary-ramp-foreground font-extrabold text-2xl md:text-3xl leading-[1.3] text-balance",
      h2: `${HEADING_BASE} text-xl md:text-2xl !leading-[1.1] text-pretty`,
      h3: `${HEADING_BASE} text-lg font-semibold text-pretty`,
      h4: `${HEADING_BASE} text-base font-semibold text-pretty`,
      h3Emphasis: "text-content-800 text-lg font-semibold text-pretty",
    },
    body: {
      default: `text-base ${BODY_BASE}`,
      secondary: "text-neutral-600",
      muted: "text-neutral-500",
      onPrimary:
        "text-base md:text-lg text-primary-ramp-foreground leading-relaxed",
    },
    listingCount:
      "font-bold text-xl md:text-2xl !leading-[1.1] text-center text-secondary-700",
    label: "block text-base font-bold text-neutral-700",
    error: "text-red-600 text-sm",
    link: `text-content-700 hover:text-content-900 font-semibold underline underline-offset-2 ${FEEDBACK} rounded-sm ${FOCUS_RING}`,
    extraSmall: "text-xs",
    small: "text-sm",
    bold: "font-bold",
    emphasis: "font-medium text-neutral-800",
    price: "font-semibold text-secondary-600 text-base tabular-nums",
    priceProminent:
      "font-semibold text-secondary-600 text-xl sm:text-2xl tabular-nums",
    success: "text-green-600",
    warning: "text-yellow-800",
    // Where an order's payment stands, as icon + label in the same size as
    // `elapsed` beside it. `data-tone` is the payment status tone; each
    // colour clears 4.5:1 on white at this size.
    paymentState:
      "inline-flex items-center gap-2 text-sm font-semibold text-neutral-600 data-[tone=green]:text-green-700 data-[tone=red]:text-red-700 data-[tone=yellow]:text-yellow-800 data-[tone=blue]:text-blue-700",
    // Time elapsed since something happened; `data-late="true"` marks one
    // that has waited too long.
    elapsed:
      "inline-flex items-center gap-2 text-sm text-neutral-600 data-[late=true]:font-bold data-[late=true]:text-yellow-800",
    pageSubtitle: "text-xl text-neutral-600",
    highlightLabel: "text-base text-content-900",
    highlightValue: "text-4xl font-extrabold text-content-700",
    highlightCaption: "text-sm text-content-900",
    moneyColor: "text-secondary-600",
    brandEmphasis: "text-content-700",
    brandEmphasisLight: "text-content-600",
    brandEmphasisHover: "hover:text-content-700",
    pillLinkHover: "hover:bg-tint-50 hover:text-content-900",
    linkOnPrimary: "hover:text-content-100",
    brandMark: "text-xl md:text-2xl font-bold truncate min-w-0",
    storeBrand: "text-secondary-900",
    storeBrandHover: "hover:text-secondary-900",
    storeBrandEmphasis: "text-secondary-700",
  },
  containers: {
    fullscreenCentered:
      "flex items-center justify-center min-h-screen bg-neutral-100",
    orderSummary: "pt-4 border-t border-neutral-200 flex flex-col gap-2",
    hairlineDivider: "border-t border-neutral-200 pt-4",
    pageBackground: "bg-neutral-ground",
    // A list's controls (tabs, search) pinned to the top of the viewport while
    // its rows scroll beneath, on the page ground so rows don't show through.
    stickyToolbar: "sticky top-0 z-10 py-2",
    // The desktop detail pane beside a list: stays in view below the sticky
    // toolbar and scrolls on its own when the record is taller than the
    // screen.
    detailPane:
      "lg:sticky lg:top-24 lg:max-h-[calc(100dvh-7rem)] lg:overflow-y-auto lg:overscroll-contain",
    brandDivider: "border-tint-50",
    editingBorder: "border-primary-500",
    // The one way a surface scrolls inside itself: a thin neutral scrollbar
    // (`.scrollbar-quiet` in global.css) and no scroll chaining to the page.
    scrollArea: "overflow-y-auto overscroll-contain scrollbar-quiet",
  },
  spacing: {
    // A layout's header, main and footer are siblings of `frame`: its gap is
    // the only interval between them, so no band pads toward its neighbour.
    page: {
      frame: `flex flex-col ${GAP.responsive}`,
      x: "px-4",
      header: "py-4",
      end: "pb-4 sm:pb-8",
    },
    gap: GAP,
    stack: {
      xs: `${STACK} ${GAP.xs}`,
      sm: `${STACK} ${GAP.sm}`,
      md: `${STACK} ${GAP.md}`,
      lg: `${STACK} ${GAP.lg}`,
      responsive: `${STACK} ${GAP.responsive}`,
    },
    // Put it on a plain block with auto width: combined with `w-full` (or any
    // fixed width) the negative margins shift the box instead of widening it,
    // leaving a gutter on the far side.
    bleed: {
      mobile: "-mx-4 sm:mx-0",
      // Cancels `cardRecipe({ variant: "row" })`'s `p-3 sm:p-4` on the
      // leading, top and bottom sides so media sits flush with the row's
      // edge. Keep in step with that inset.
      rowStart: "-my-3 -ml-3 sm:-my-4 sm:-ml-4",
      // Cancels `cardRecipe({ variant: "shell" })`'s end and bottom inset and
      // puts it back inside, so a scroll area inside a shell carries its
      // scrollbar on the shell's edge instead of floating in its padding.
      // Keep in step with that inset.
      scrollRegion:
        "-mr-4 pr-4 -mb-4 pb-4 sm:-mr-6 sm:pr-6 sm:-mb-6 sm:pb-6 lg:-mr-8 lg:pr-8 lg:-mb-8 lg:pb-8",
    },
  },
  lists: {
    container: "divide-y divide-neutral-200",
    // The list's own edges take the parent's gap, so only the dividers
    // between rows carry padding.
    item: "py-4 first:pt-0 last:pb-0",
    // A row in a long list that skips layout and paint while off-screen;
    // the intrinsic size is a typical row's height, remembered once shown.
    offscreenRow:
      "[content-visibility:auto] [contain-intrinsic-size:auto_12rem]",
    // The row whose details the desktop pane is showing (`data-selected`).
    selectedRow:
      "lg:data-[selected=true]:border-primary-600 lg:data-[selected=true]:ring-1 lg:data-[selected=true]:ring-primary-600",
  },
  images: {
    coverZoom: IMAGE_COVER_ZOOM,
    productMain: "overflow-hidden sm:rounded-lg",
    thumbnailGrid: "grid grid-cols-4 gap-4",
    uploadGrid: "grid grid-cols-2 sm:grid-cols-4 gap-4",
    thumbnailButton: `relative group cursor-pointer rounded-lg overflow-hidden ${STATE} hover:scale-105 outline-none ${FOCUS_RING}`,
    activeThumbnailRing: "ring-2 ring-primary-600 ring-offset-2",
    activeThumbnailOverlay: "absolute inset-0 bg-primary-600/25 rounded-lg",
    // Fills the row's height (pair with `spacing.bleed.rowStart`); the
    // leading radius matches `cardRecipe({ variant: "row" })`'s corners.
    rowThumbnail:
      "w-16 sm:w-20 self-stretch object-cover rounded-l-xl md:rounded-l-2xl",
    thumbnailSm: "w-16 h-16 object-cover rounded-md",
    // Stands in for `thumbnailSm` when an item has no photo.
    thumbnailPlaceholder:
      "flex w-16 h-16 items-center justify-center rounded-md bg-neutral-100 text-neutral-500",
    thumbnailFull: "w-full h-auto rounded-lg",
    // The "add a photo" tile of an upload grid. It spans the whole grid while
    // the picker is empty (`data-empty="true"`) and is one square cell beside
    // the thumbnails after; `data-state="over"` marks a file dragged over it.
    // It is the visible label of an `sr-only` file input placed just before
    // it, so the input's keyboard focus shows here through `peer-*`.
    dropZone: `flex flex-col items-center justify-center gap-2 w-full p-4 text-center text-neutral-700 rounded-lg border-2 border-dashed border-neutral-300 bg-neutral-50 cursor-pointer select-none hover:border-primary-600 hover:bg-primary-50 data-[state=over]:border-primary-600 data-[state=over]:bg-primary-50 data-[empty=true]:min-h-40 data-[empty=true]:col-span-2 sm:data-[empty=true]:col-span-4 data-[empty=false]:aspect-square peer-focus-visible:ring-2 peer-focus-visible:ring-primary-500 peer-focus-visible:ring-offset-2 ${STATE}`,
    dropZoneIcon: "text-primary-600",
    // A thumbnail's "+2 more" count, pinned to its bottom-right corner.
    countBadge:
      "pointer-events-none absolute -bottom-1 -right-1 inline-flex items-center justify-center min-w-6 h-6 rounded-full bg-neutral-900 px-1 text-sm font-bold text-white tabular-nums ring-2 ring-white",
    mainBadge:
      "pointer-events-none absolute bottom-2 left-2 rounded-full bg-white px-2 py-0.5 text-sm font-semibold text-neutral-800 shadow-sm",
  },
  // A checklist's completion bar: one segment per item, filled by the
  // segment's own `data-state="done"`.
  progress: {
    track: "flex gap-1",
    segment: `h-2 flex-1 rounded-full bg-neutral-200 data-[state=done]:bg-primary-600 ${NOTICE}`,
  },
  // A small pill that performs an action, e.g. jumping to a missing field.
  chips: {
    action: `inline-flex items-center gap-1 min-h-11 rounded-full border border-neutral-300 bg-white px-3 text-base font-semibold text-neutral-800 hover:border-primary-600 hover:text-primary-700 ${STATE} ${FOCUS_RING}`,
  },
  // Mock-ups of where a product shows up outside the editor: its store
  // card, a Google result and a WhatsApp link preview.
  preview: {
    card: "relative block w-full max-w-xs mx-auto overflow-hidden rounded-xl md:rounded-2xl border border-neutral-200 bg-neutral-100",
    cardEmptyImage:
      "flex w-full aspect-square items-center justify-center text-neutral-400",
    searchSite: "text-sm text-neutral-700 truncate",
    searchTitle: "text-xl leading-snug text-blue-800 line-clamp-2 break-words",
    searchSnippet: "text-sm text-neutral-600 line-clamp-3 break-words",
    chatGround: "rounded-lg bg-neutral-100 p-3",
    chatBubble: "overflow-hidden rounded-lg bg-white shadow-sm",
    chatImage: "w-full aspect-[1200/630] object-cover bg-neutral-200",
    chatText: "flex flex-col gap-1 p-3",
    chatTitle: "font-semibold text-neutral-900 line-clamp-2 break-words",
    chatMeta: "text-sm text-neutral-600 line-clamp-2 break-words",
    placeholder: "text-neutral-500 italic",
  },
  // A row of a few at-a-glance numbers sharing one hairline surface, each
  // cell split from the next by a divider rather than boxed on its own.
  // At-a-glance numbers as a row of small cards
  // (`src/components/manager/StatStrip.jsx`). A cell's own `data-tone`
  // styles its number; a "money" cell takes a full row on phones, where a
  // large amount can't fit a third of a 320px screen, and sits in line from
  // `sm`.
  stats: {
    strip: "flex flex-wrap gap-2 sm:gap-4",
    cell: "group flex min-w-0 flex-1 basis-0 flex-col items-center gap-1 p-3 text-center data-[tone=money]:basis-full sm:data-[tone=money]:basis-auto",
    // A cell that is not a link; links take `cardRecipe({ variant: "interactive" })`.
    surface: "bg-white border border-neutral-200 rounded-xl md:rounded-2xl",
    label: "text-sm text-neutral-700",
    value:
      "text-3xl font-bold leading-tight tabular-nums text-secondary-700 group-data-[tone=attention]:text-yellow-800",
  },
  // A short confirmation along the bottom edge that can offer one action
  // (Undo). Rises above the phone's home indicator.
  toast: {
    viewport:
      "fixed inset-x-0 bottom-0 z-50 flex flex-col gap-2 px-4 pb-[max(1rem,env(safe-area-inset-bottom))] outline-none sm:left-auto sm:w-96",
    root: `flex items-center gap-3 rounded-lg bg-neutral-900 py-2 pl-4 pr-2 text-white shadow-lg ${ENTRANCE} data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=cancel]:translate-x-0 ${NOTICE}`,
    message: "min-w-0 flex-1 text-base",
    action: `inline-flex shrink-0 items-center justify-center min-h-11 min-w-11 rounded-md px-3 font-bold text-white underline underline-offset-2 hover:bg-neutral-700 disabled:opacity-50 ${FEEDBACK} focus:outline-none focus-visible:ring-2 focus-visible:ring-white`,
  },
  summary: {
    row: "flex justify-between items-center text-lg",
    totalRow:
      "flex justify-between items-center text-lg font-bold tabular-nums",
  },
  productCard: {
    container: `relative block ${NOTICE} focus:outline-none`,
    overlay: `absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/60 to-transparent px-4 pt-12 pb-4 flex flex-col items-start gap-1 text-left`,
    name: "text-base font-semibold text-white leading-snug line-clamp-2 text-pretty",
    price: "text-base font-semibold text-white leading-none tabular-nums",
  },
  icons: {
    circleBackground:
      "inline-flex items-center justify-center p-4 rounded-full",
    badgePrimary: "bg-primary-100 text-primary-700",
    tint: "text-primary-600",
    tintMuted: "text-primary-400",
    // An icon inside a text field's left padding (search). A tap on it still
    // lands on the field.
    inputLeading:
      "pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-500",
    badgeMuted: "bg-primary-50",
    celebrateBg: "bg-secondary-300",
  },
  badges: {
    countPill: "bg-secondary-700",
    categoryPill:
      "inline-flex items-center rounded-full bg-primary-50 px-2 py-0.5 text-sm font-semibold text-primary-700",
    tag: "inline-flex items-center rounded-md bg-neutral-100 px-3 py-1 text-sm text-neutral-700",
  },
  skeletons: {
    card: "bg-white p-6 rounded-xl md:rounded-2xl shadow animate-pulse",
    bar: "rounded-md bg-neutral-300 animate-pulse",
  },
  settings: {
    heading: "text-2xl md:text-3xl font-bold text-neutral-800",
    intro: "text-neutral-600",
    grid: `${STACK} md:grid-cols-2 ${GAP.responsive}`,
    // Store Settings: below `lg` the section index and the open section
    // take turns (the root's `data-view` says which shows); from `lg` both
    // sit side by side as a sticky sidebar and a panel.
    layout: `${STACK} ${GAP.responsive} lg:grid-cols-4`,
    navColumn:
      "lg:col-span-1 lg:sticky lg:top-8 lg:self-start data-[view=section]:hidden lg:data-[view=section]:block",
    panelColumn: `lg:col-span-3 min-w-0 ${STACK} ${GAP.md} data-[view=list]:hidden lg:data-[view=list]:grid focus:outline-none`,
    navGroupLabel: "text-sm font-bold uppercase tracking-wide text-neutral-600",
    navIcon:
      "flex items-center justify-center shrink-0 w-10 h-10 rounded-lg bg-primary-50 text-primary-700 lg:w-auto lg:h-auto lg:bg-transparent lg:text-current",
    navHint: "block text-sm text-neutral-600 lg:hidden",
    navChevron: "shrink-0 text-neutral-500 lg:hidden",
    backToIndex: "lg:hidden",
    divider: "border-t border-neutral-200",
  },
  filters: {
    trigger: `inline-flex items-center justify-center gap-1 px-3 py-2 min-h-11 rounded-md bg-white border border-neutral-300 text-neutral-700 hover:text-neutral-900 hover:border-neutral-400 hover:shadow-sm ${STATE} ${FOCUS_RING}`,
    // No margin-top: Radix DropdownMenu.Content positions itself via
    // sideOffset, so a margin here would stack on top of that and double
    // the gap.
    panel: `w-56 bg-white rounded-lg shadow-lg border border-neutral-200 p-2 ${STATE} hover:shadow-xl`,
    optionDestructive:
      "text-red-700 hover:bg-red-50 hover:text-red-800 focus:bg-red-50 focus:text-red-800",
    option: `w-full min-h-11 text-left px-3 py-2 rounded-md hover:bg-neutral-100 hover:text-neutral-900 focus:bg-neutral-100 focus:outline-none ${FEEDBACK} data-[state=checked]:bg-primary-50 data-[state=checked]:text-primary-700 data-[state=checked]:hover:bg-primary-100`,
  },
  motion: {
    feedback: FEEDBACK,
    state: STATE,
    notice: NOTICE,
    entrance: ENTRANCE,
    celebrate: CELEBRATE,
    // A touch press on a large tappable area with no fill of its own.
    press: `active:opacity-70 ${STATE}`,
    tumble: "animate-tumble",
    tumbleTag: "animate-tumble-tag",
    dust: "animate-dust",
  },
  utilities: {
    lineClamp2: "line-clamp-2",
    truncate: "truncate",
  },
  a11y: {
    focusRing: FOCUS_RING,
    // `w-fit` keeps the target content-sized inside a `stack.*` grid, which
    // blockifies `inline-flex` and stretches it across the column.
    tapTarget:
      "inline-flex w-fit items-center justify-center min-h-11 min-w-11",
    tapTargetInline: "inline-flex w-fit items-center min-h-11",
    inlineOutset: "-mx-2 px-2",
    tapHighlightNone: "[-webkit-tap-highlight-color:transparent]",
    // A drag that starts on a press and hold must not also open the
    // phone's own long-press menu or start selecting text.
    longPressSafe: "select-none [-webkit-touch-callout:none]",
    srOnly: "sr-only",
    stickyHeaderOffset: "scroll-mt-20",
  },
  actionBar: {
    surface: `bg-white border-t border-neutral-300 px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] ${NOTICE}`,
    hidden: "translate-y-full opacity-0 pointer-events-none",
    visible: "translate-y-0 opacity-100",
    clearance: "pb-36 md:pb-8",
  },
}
